import { useIntl } from 'react-intl'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowLuxPlusNonMemberPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Group from 'components/utils/Group'
import { getExperienceTileSaleUnit } from 'lib/experiences/getExperienceTileSaleUnit'
import { getOfferValueDisplayVisibility } from 'lib/offer/getOfferDisplayPricing'
import FormatCurrency from 'components/Common/FormatCurrency'
import { floatify } from 'lib/maths/mathUtils'
import Caption from 'LuxKit/Typography/Caption'
import getExperienceAdditionalDiscount from 'lib/experiences/getExperienceAdditionalDiscount'

interface MappedProps {
  isLuxPlusEnabled: boolean
}

interface Props extends MappedProps {
  totalPrice?: number
  totalMemberPrice?: number
  totalGrossPrice?: number
  discountPercentage?: number
  isFromPrice: boolean
  showFromCaption?: boolean
  ticketUnitLabel: string
  size?: 'S' | 'L' | 'M'
  nonMemberPriceSize?: 'L' | 'M'
  discountMemberPercentage?: number
  additionalDiscountPercentage?: number
  additionalDiscountAmount?: number
  additionalMemberDiscountAmount?: number
}

function ExperienceTilePrices(props: Props) {
  const {
    totalPrice,
    totalMemberPrice,
    totalGrossPrice,
    discountPercentage,
    isFromPrice,
    showFromCaption = true,
    ticketUnitLabel,
    isLuxPlusEnabled,
    size = 'S',
    nonMemberPriceSize = 'M',
    discountMemberPercentage,
    additionalDiscountPercentage = 0,
    additionalDiscountAmount,
    additionalMemberDiscountAmount,
  } = props
  const intl = useIntl()

  const fromCaption = intl.formatMessage({
    id: 'EAQ_GV7KRwveLWB08EmL',
    defaultMessage: 'From',
    description: 'Price caption prefix on the experience tile prices',
  })
  const totalLabel = intl.formatMessage({
    id: 'whMf0sOzbI-AWwhc2zR7',
    defaultMessage: 'total',
    description: 'Sale unit label for total price on experience tile prices',
  })
  const saleUnit = getExperienceTileSaleUnit({ isFromPrice, ticketUnitLabel, totalLabel })
  const discountType = isFromPrice ? 'estimate' : 'exact'
  const {
    shouldShowDiscount: shouldShowMemberDiscount,
  } = getOfferValueDisplayVisibility({ discountPercent: discountMemberPercentage })
  const {
    shouldShowDiscount,
    shouldShowValue,
  } = getOfferValueDisplayVisibility({
    discountPercent: discountPercentage,
    value: totalGrossPrice,
  })

  const usesMemberPrice = isLuxPlusEnabled && !!totalMemberPrice
  const originalPrice = usesMemberPrice ? totalMemberPrice : totalPrice
  const hasAdditionalDiscount = additionalDiscountPercentage > 0 && additionalDiscountPercentage < 100
  if (hasAdditionalDiscount && originalPrice) {
    const discount = (usesMemberPrice ? additionalMemberDiscountAmount : additionalDiscountAmount) ?? getExperienceAdditionalDiscount(originalPrice, additionalDiscountPercentage)
    const discountedPrice = floatify(originalPrice - discount)
    const previousPrice = <s><FormatCurrency value={originalPrice} format="dynamicDollar" /></s>
    return <>
      {showFromCaption && <PriceRowPriceCaption>
        {isFromPrice ? intl.formatMessage({
          id: 'DnpmEnO5q2RFi9YHevlo',
          defaultMessage: 'From {originalPrice}',
          description: 'Caption on a discounted experience tile showing its previous starting price with a strikethrough',
        }, { originalPrice: previousPrice }) : previousPrice}
      </PriceRowPriceCaption>}
      {usesMemberPrice ? <PriceRowLuxPlusPrice
        logoPlacement="trailing"
        size={size}
        memberPrice={discountedPrice}
        priceFormat="dynamicDollar"
        saleUnit={saleUnit}
      /> : <PriceRowPrice
        size={size}
        price={discountedPrice}
        priceFormat="dynamicDollar"
        saleUnit={saleUnit}
        wrap="word-wrap"
      />}
      {usesMemberPrice && !!totalPrice && <Caption variant="medium" colour="light-foreground-alt">
        {intl.formatMessage({
          id: 'jW8V7BSUx0LTJ4S2ds-T',
          defaultMessage: 'Non-member {price}',
          description: 'Non-member price after an additional checkout discount on an experience recommendation tile',
        }, { price: <b><FormatCurrency value={floatify(totalPrice - (additionalDiscountAmount ?? getExperienceAdditionalDiscount(totalPrice, additionalDiscountPercentage)))} format="dynamicDollar" /></b> })}
      </Caption>}
    </>
  }

  if (isLuxPlusEnabled && totalMemberPrice) {
    return (
      <>
        {isFromPrice && showFromCaption && <PriceRowPriceCaption>{fromCaption}</PriceRowPriceCaption>}
        <PriceRowLuxPlusPrice
          logoPlacement="trailing"
          size={size}
          memberPrice={totalMemberPrice}
          saleUnit={saleUnit}
        />
        {!!totalPrice && (
          <PriceRowLuxPlusNonMemberPrice
            nonMemberPrice={totalPrice}
          />
        )}
        {!!totalGrossPrice && <PriceRowValueDiscountWithCaption
          originalValue={totalGrossPrice}
          size={nonMemberPriceSize}
          discountPercentage={shouldShowMemberDiscount ? discountMemberPercentage : undefined}
          discountType={discountType}
        />}
      </>
    )
  }

  if (!totalPrice) return null

  return (
    <>
      {isFromPrice && showFromCaption && <PriceRowPriceCaption>{fromCaption}</PriceRowPriceCaption>}
      <Group direction="horizontal" gap={8} wrap="wrap" verticalAlign="baseline">
        <PriceRowPrice
          size={size}
          price={totalPrice}
          saleUnit={saleUnit}
          wrap="word-wrap"
        />
        {shouldShowValue && totalGrossPrice !== undefined && <PriceRowValueDiscountWithCaption
          originalValue={totalGrossPrice}
          size={nonMemberPriceSize}
          discountPercentage={shouldShowDiscount ? discountPercentage : undefined}
          discountType={discountType}
        />}
      </Group>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(ExperienceTilePrices)
